import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRFIContext } from "../../../context/domain/operations/RFIContext";
import { Button, WorkerInitials } from "../../../components/ui";
import { TooltipArrow } from "../../../components/ui";
import {
  TicketLogTable,
  TicketLogRow,
  TicketCard,
  useTicketClone,
} from "../../../features/tickets";
import { CreateRFIModal } from "../../../features/operations/rfi";
import {
  RFI_STATUS_COLORS_LIGHT,
  RFI_STATUS_LABELS,
} from "../../../features/operations/rfi/constants";
import { formatDate } from "../../../utils/datetime";
import { createRFI, getRFIById, deleteRFI } from "../../../services/api/rfi";

/**
 * RFI Log List Page
 * Displays all RFIs in a table or card format
 *
 * Uses: TicketLogTable from generic ticket system
 * Uses: TicketCard for card view mode
 * Uses: useRFIContext from createTicketContext factory
 * Uses: CreateRFIModal for new RFI creation
 */
const RFILog = () => {
  const navigate = useNavigate();
  const { items: rfis, loading, highlightAddedItemId, highlightDeletedItemId, reportNewItem, executeDelete } = useRFIContext();
  const [filteredCount, setFilteredCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { cloneTicket } = useTicketClone({
    fetchById: getRFIById,
    createTicket: createRFI,
    reportNewItem,
    transformPayload: (payload) => {
      payload.status = "new";
      return payload;
    },
  });

  const handleClone = async (rfi) => {
    try {
      await cloneTicket(rfi.id);
    } catch (error) {
      // Error handling is done in the hook (console.error only, no UI messages)
    }
  };

  useEffect(() => {
    document.title = "RFI Log - R&S";
  }, []);

  const handleCreateNew = () => {
    setShowCreateModal(true);
  };

  const handleRowClick = (rfi) => {
    navigate(`/operations/rfi/${rfi.id}`);
  };

  // Column configuration for TicketLogTable
  const columns = [
    {
      key: "rfi_id",
      label: "RFI",
      width: "11%",
      sortable: true,
      cellClassName: "p-2 py-3 pl-4 font-mono text-sm font-medium text-blue-600",
    },
    {
      key: "status",
      label: "Status",
      width: "12%",
      sortable: true,
      cellClassName: "p-2 py-3",
      render: (item) => {
        const status = item.status || "new";
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${RFI_STATUS_COLORS_LIGHT[status] || "bg-gray-100"}`}
          >
            {RFI_STATUS_LABELS[status] || status}
</span>
        );
      },
    },
    {
      key: "subject",
      label: "Summary",
      width: "32%",
      sortable: true,
      truncate: true,
      cellClassName: "p-2 py-3 max-w-0",
    },
    {
      key: "job_number",
      label: "Project",
      maxWidth: "150px",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "p-2 py-3",
      render: (item) =>
        item.job_number ? `${item.job_number} - ${item.job_description}` : "-",
    },
    {
      key: "spec_reference",
      label: "Spec",
      width: "88px",
      maxWidth: "120px",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "p-2 py-3 whitespace-nowrap",
      render: (item) => item.spec_reference || "-",
    },
    {
      key: "drawing_reference",
      label: "Drawing",
      width: "88px",
      maxWidth: "120px",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "p-2 py-3 whitespace-nowrap",
      render: (item) => item.drawing_reference || "-",
    },
    {
      key: "priority",
      label: "Priority",
      width: "60px",
      maxWidth: "60px",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "p-2 py-3 text-center",
      render: (item) => {
        const priority = item.priority || "medium";
        // Map priority to emoji circles: Low = 🟢, Medium = 🟡, High = 🔴
        const priorityEmoji = {
          low: "🟢",
          medium: "🟡",
          high: "🔴",
          critical: "🔴", // Map critical to red as well
        };
        // Map priority to display text
        const priorityLabels = {
          low: "Low",
          medium: "Medium",
          high: "High",
          critical: "High", // Map critical to High for display
        };
        const emoji = priorityEmoji[priority] || "🟡";
        const label = priorityLabels[priority] || "Medium";
        return (
          <span className="text-lg inline-block relative group cursor-pointer">
            {emoji}
            <TooltipArrow
              text={label}
              position="above"
              className="text-white bg-gray-900"
            />
          </span>
        );
      },
    },
    {
      key: "created_by",
      label: "By",
      width: "44px",
      maxWidth: "48px",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "p-2 py-3 text-center",
      getSortValue: (item) => item.created_by?.name ?? "",
      render: (item) => {
        if (item.created_by) {
          return (
            <WorkerInitials
              worker={item.created_by}
              tooltip
            />
          );
        }
        return "-";
      },
    },
    {
      key: "submitted_at",
      label: "Submitted",
      width: "92px",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "p-2 py-3 whitespace-nowrap",
      render: (item) => {
        if (!item.submitted_at) return "-";
        try {
          return formatDate(item.submitted_at, "M/D/YY");
        } catch {
          return "-";
        }
      },
    },
    {
      key: "required_by_date",
      label: "Due",
      width: "88px",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "p-2 py-3 whitespace-nowrap",
      render: (item) => {
        if (!item.required_by_date) return "-";
        try {
          // Normalize dates to midnight for accurate date-only comparison
          const dueDate = new Date(item.required_by_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          dueDate.setHours(0, 0, 0, 0);
          
          // Check if due date is in the past
          const isOverdue = dueDate < today;
          // Check if RFI is not closed (closed RFIs don't need red styling even if overdue)
          const isNotClosed = item.status !== "closed";
          // Show red text only if overdue AND not closed
          const shouldShowRed = isOverdue && isNotClosed;
          
          const formattedDate = formatDate(item.required_by_date, "M/D/YY");
          return (
            <span className={shouldShowRed ? "text-red-600 font-medium" : ""}>
              {formattedDate}
            </span>
          );
        } catch {
          return "-";
        }
      },
    },
    {
      key: "actions",
      label: "", // No header label
      width: "50px",
      cellClassName: "px-2 py-4 text-center",
      hideOnMobile: false,
      isActions: true, // Flag to identify actions column
    },
  ];

  // Status filter options
  const statusFilters = [
    { value: "new", label: RFI_STATUS_LABELS.new },
    { value: "open", label: RFI_STATUS_LABELS.open },
    { value: "awaiting_response", label: RFI_STATUS_LABELS.awaiting_response },
    { value: "closed", label: RFI_STATUS_LABELS.closed },
  ];

  // Custom status filter function
  const statusFilterFn = (item, status) => {
    return (item.status || "new") === status;
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="p-2 md:p-4 max-w-[1420px] md:min-w-[1240px] mx-auto">
      {/* Header */}
      <div className="bg-blue-900 text-white p-4 rounded-t-lg flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">RFI Log</h1>
          <p className="text-sm pr-2">Request for Information Log</p>
        </div>
        <div>
          <Button
            variant="custom"
            text="Create"
            icon="plus"
            onClick={handleCreateNew}
            className="md:hidden px-4 py-2 bg-blue-600 text-white hover:bg-blue-700"
          />
          <Button
            variant="custom"
            text="Create New RFI"
            icon="plus"
            onClick={handleCreateNew}
            className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white hover:bg-blue-700"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-b-lg shadow">
        <TicketLogTable
          data={rfis}
          columns={columns}
          RowComponent={TicketLogRow}
          CardComponent={TicketCard}
          onRowClick={handleRowClick}
          searchFields={["rfi_id", "subject", "description", "job_number"]}
          searchPlaceholder="Search by RFI ID, subject, description..."
          statusFilters={statusFilters}
          statusFilterFn={statusFilterFn}
          onFilteredCountChange={setFilteredCount}
          emptyMessage="No RFIs found"
          defaultSort={{ field: "created_at", direction: "desc" }}
          defaultView="group"
          groupBy="job_number"
          getGroupLabel={(groupValue, items) => {
            // Format group label for project grouping
            if (!groupValue) return "No Project";
            const job = items[0]?.job_number 
              ? `${items[0].job_number} - ${items[0].job_description || ''}`.trim()
              : `Project ${groupValue}`;
            return job || "Unknown Project";
          }}
          highlightAddedItemId={highlightAddedItemId}
          highlightDeletedItemId={highlightDeletedItemId}
          onDelete={(item) => deleteRFI(item.id)}
          executeDelete={executeDelete}
          rowProps={{
            onClone: handleClone,
          }}
          cardProps={{
            onClone: handleClone,
          }}
        />

        {/* Footer */}
        <div className="flex justify-between items-center rounded-b-lg p-4 bg-gray-100 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing <b className="text-blue-800">{filteredCount}</b> of{" "}
            <b className="text-blue-800">{rfis.length}</b> RFI
            {rfis.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Create RFI Modal */}
      <CreateRFIModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  );
};

export default RFILog;
